/**
 * GaugePopup - Popup for USGS stream gauge stations on the map.
 */
import { X, Loader2, Heart } from 'lucide-react';

// Inline Sparkline SVG component
function Sparkline({ data, width = 220, height = 40, color = '#3b82f6' }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d.value ?? d);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="w-full">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
    </svg>
  );
}

export default function GaugePopup({ gauge, getTrendInfo, onClose, onSave }) {
  return (
    <div className="min-w-[250px] max-w-[300px] p-3">
      <button
        onClick={onClose}
        className="absolute top-2 right-2 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
        data-testid="gauge-popup-close"
      >
        <X className="w-4 h-4" />
      </button>

      <h3 className="font-semibold text-base text-gray-900 mb-1 pr-6">
        {gauge.site_name}
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        USGS Site: {gauge.site_id}
      </p>

      {gauge.loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-sm text-gray-500">Loading conditions...</span>
        </div>
      ) : gauge.error ? (
        <div className="bg-red-50 rounded-lg p-3 mb-3 text-center">
          <p className="text-sm text-red-600">Unable to load conditions</p>
          <p className="text-xs text-red-500 mt-1">Please try again later</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: gauge.color || '#9ca3af' }}
            />
            <span className="text-sm font-medium text-gray-700">
              {gauge.condition_label || 'Unknown Condition'}
            </span>
          </div>

          {gauge.discharge_cfs !== undefined && gauge.discharge_cfs !== null && (
            <div className="bg-gray-50 rounded-lg p-2 mb-3">
              <p className="text-xs text-gray-500">Current Discharge</p>
              <p className="text-lg font-bold text-gray-900">
                {gauge.discharge_cfs.toLocaleString()} <span className="text-sm font-normal">cfs</span>
              </p>
            </div>
          )}

          {gauge.gage_height_ft !== undefined && gauge.gage_height_ft !== null && (
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Gage Height:</span>
              <span className="font-medium">{gauge.gage_height_ft.toFixed(2)} ft</span>
            </div>
          )}

          {gauge.water_temp_f !== undefined && gauge.water_temp_f !== null && (
            <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
              <span>Water Temp:</span>
              <span className="font-medium">{gauge.water_temp_f}°F</span>
            </div>
          )}

          {gauge.sparkline_data && gauge.sparkline_data.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-2 mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">7-Day Trend</span>
                {gauge.trend_7d && (
                  <span className={`text-xs font-medium flex items-center gap-1 ${getTrendInfo(gauge.trend_7d).color}`}>
                    {getTrendInfo(gauge.trend_7d).icon}
                    {getTrendInfo(gauge.trend_7d).label}
                  </span>
                )}
              </div>
              <Sparkline
                data={gauge.sparkline_data}
                width={220}
                height={40}
                color={gauge.color || '#3b82f6'}
              />
              {gauge.stats_7d && (
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>Min: {gauge.stats_7d.min?.toLocaleString()} cfs</span>
                  <span>Max: {gauge.stats_7d.max?.toLocaleString()} cfs</span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!gauge.loading && (
        <>
          <button
            onClick={onSave}
            className="w-full mb-3 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
            data-testid="gauge-save-in-app-btn"
          >
            <Heart className="w-4 h-4" />
            Save in the app
          </button>

          <a
            href={`https://waterdata.usgs.gov/nwis/uv?site_no=${gauge.site_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
            data-testid="gauge-usgs-link"
          >
            View on USGS
          </a>
        </>
      )}
    </div>
  );
}
